/**
 * The page's half of the service worker.
 *
 * Updating is the whole difficulty. A worker that pins a driver to a
 * three-week-old build is worse than no worker at all, and a driver cannot be
 * asked to notice a banner, understand it, and reload — so nothing here is
 * offered to them. The chosen strategy is skipWaiting + clients.claim in sw.ts,
 * and a one-shot reload here when the new worker takes over.
 *
 * The reload is what makes claiming safe. Once a new worker controls this page
 * it is serving a different build's assets, and a lazily-imported route from
 * the old build no longer exists in the cache or on the server; the page has to
 * come back on the new build rather than limp along on the old one. It is
 * skipped on the first-ever visit, where claiming an uncontrolled page means
 * nothing has changed.
 *
 * The trade is that a deploy can land on a driver mid-form and take what they
 * had typed. Deploys are rare, the window is the second or two after
 * activation, and the alternative — a build that reaches a phone whenever its
 * owner happens to restart the browser — is the failure that matters.
 */

const SW_URL = '/sw.js';

/**
 * A driver leaves the round open on the passenger seat all day, and a browser
 * only looks for a new worker on navigation. Coming back to the tab is the
 * closest thing to a navigation there is.
 */
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

export function registerServiceWorker(): void {
  // The second half is the secure-context rule said out loud: the browser
  // already withholds the API off https, localhost excepted.
  if (!window.isSecureContext || !('serviceWorker' in navigator)) return;

  if (import.meta.env.DEV) {
    // A worker left over from a preview build on this port would serve a frozen
    // copy of the app over the dev server and cost somebody an afternoon.
    void tearDown();
    return;
  }

  // Registration competes with the first paint a driver is waiting on, and the
  // precache is the whole bundle a second time.
  if (document.readyState === 'complete') {
    void start();
  } else {
    window.addEventListener('load', () => void start(), { once: true });
  }
}

async function start(): Promise<void> {
  // Read before registering: on a first visit there is no controller, and the
  // new worker claiming this page does not mean the page is out of date.
  const wasControlled = navigator.serviceWorker.controller !== null;
  let reloading = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!wasControlled || reloading) return;
    reloading = true;
    window.location.reload();
  });

  try {
    const registration = await navigator.serviceWorker.register(SW_URL, {
      scope: '/',
      // Browsers may serve sw.js itself from the HTTP cache for up to a day.
      // The one file that must never be stale is the one that decides what
      // stale means.
      updateViaCache: 'none',
    });
    watchForUpdates(registration);
  } catch (error) {
    // Never fatal: the back office works online without a worker.
    console.error('Service worker registration failed', error);
  }
}

function watchForUpdates(registration: ServiceWorkerRegistration): void {
  let lastCheck = Date.now();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastCheck < UPDATE_CHECK_INTERVAL_MS) return;
    lastCheck = Date.now();
    // Offline, or nothing deployed: either way the next time the driver comes
    // back to the tab asks again.
    void registration.update().catch(() => undefined);
  });
}

async function tearDown(): Promise<void> {
  // This page is still whatever the old worker decided to serve it; unregistering
  // only takes effect on the next load. Reloading once makes the recovery whole,
  // and cannot loop: the reloaded page has no controller left to find.
  const controlled = navigator.serviceWorker.controller !== null;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    const names = await caches.keys();
    await Promise.all(
      names.filter((name) => name.startsWith('dryclean-')).map((name) => caches.delete(name)),
    );
  } catch (error) {
    console.error('Could not clear a stale service worker', error);
    return;
  }

  if (controlled) window.location.reload();
}
